import { isEmpty, uniq } from 'lodash';
import { message } from 'antd';
import { takeLatest, put, select, all } from 'redux-saga/effects';
import { SELECTED_METRIC_CHART } from '@store/metricsChartContainer/types';
import { METRICS_MAPPING } from '@utils';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import { DIMENSION_TYPES_CONSTANTS, METRICS_TYPES_EXCLUDE_BY_FILTER } from '@utils/metrics';
import { metricsByTypesSelector, adContentMetricsSelector } from '@store/common/selectors';
import { AD_PLAYS, isAppModule } from '@utils/constants';
import { selectedMetricChart } from '@store/metricsChartContainer/sagas';
import { dimensionMetricsSelector, dimensionsSelector, globalOrderBySelector } from './selectors';
import * as actions from './actions';
import * as types from './types';

export const metricsCountLimit = 20;

const isEIModule = window.conviva.module === 'EI';

// set/swap dimensions
function* updateDimension(action) {
  const { dimension, newDimension } = action;
  const dimensions = yield select(dimensionsSelector);
  const adContentMetrics = yield select(adContentMetricsSelector);
  const dimensionByTypes = yield select(dimensionByTypesSelector);
  const globalOrderBy = yield select(globalOrderBySelector);
  const defaultAIMetric = AD_PLAYS;

  // current dimensions index
  const currentIndex = dimensions.indexOf(dimension);

  // check if new dimension is already one of the current dimension
  const swapIndex = dimensions.indexOf(newDimension);

  if (swapIndex > -1) {
    // swap dimensions
    yield put(actions.swapSelectedDimensions(currentIndex, dimension, swapIndex, newDimension));
  } else {
    // set/unset dimensions
    yield put(actions.setSelectedDimension(currentIndex, newDimension));
  }

  // changing global order by when ad only dimensions are added
  if (
    isEIModule &&
    newDimension in dimensionByTypes &&
    dimensionByTypes[newDimension] === DIMENSION_TYPES_CONSTANTS.ai
  ) {
    if (!(globalOrderBy in adContentMetrics)) {
      yield put(actions.setGlobalOrderByMetric(defaultAIMetric));
    }
  }
}

export function* setNAMetricsForDimension(action) {
  const { dimensions = [] } = action;
  const dimensionMetrics = yield select(dimensionMetricsSelector);
  const metricsByTypes = yield select(metricsByTypesSelector);
  const dimensionByTypes = yield select(dimensionByTypesSelector);
  const hasMetricsToNA = dimensions.some(
    (d) => d in dimensionByTypes && dimensionByTypes[d] === DIMENSION_TYPES_CONSTANTS.ai,
  );

  if (!isEmpty(dimensionByTypes) && hasMetricsToNA && isEIModule) {
    const excludedMetricTypes = new Set(
      METRICS_TYPES_EXCLUDE_BY_FILTER[DIMENSION_TYPES_CONSTANTS.ai],
    );
    const filteredMetrics = dimensionMetrics.filter((metric) => {
      const metricType = metricsByTypes[metric];
      return excludedMetricTypes.has(metricType);
    });
    yield put(actions.setDimensionsMetricsToNA(filteredMetrics));
  } else {
    yield put(actions.setDimensionsMetricsToNA([]));
  }
}

function* primaryMetricEffects(action) {
  const { metricName } = action;
  const currentDimensionMetrics = yield select(dimensionMetricsSelector);

  // order by
  const globalOrderBy = METRICS_MAPPING[metricName]
    ? METRICS_MAPPING[metricName]
    : isAppModule
      ? 'AppUniqueDevices'
      : metricName;

  // table metrics
  const colMetrics = METRICS_MAPPING[metricName]
    ? [METRICS_MAPPING[metricName], metricName]
    : [metricName];
  let newDimensionMetrics = uniq([...currentDimensionMetrics, ...colMetrics]);

  // over limit
  // TODO: revisit after review with product
  if (newDimensionMetrics.length > metricsCountLimit) {
    const slicedMetrics = newDimensionMetrics
      .filter((m) => !colMetrics.includes(m))
      .slice(0, metricsCountLimit - colMetrics.length);
    newDimensionMetrics = [...slicedMetrics, ...colMetrics];
    message.warning(`You can select a maximum of ${metricsCountLimit} metrics.`);
  }

  yield all([
    put(actions.setGlobalOrderByMetric(globalOrderBy)),
    put(actions.setDimensionMetrics(newDimensionMetrics)),
  ]);
}

function* setDimensionMetrics(action) {
  const { metrics } = action;
  const dimensions = yield select(dimensionsSelector);
  const dimensionByTypes = yield select(dimensionByTypesSelector);
  const metricsByTypes = yield select(metricsByTypesSelector);

  const hasAdDimensions = dimensions.some(
    (d) => d in dimensionByTypes && dimensionByTypes[d] === DIMENSION_TYPES_CONSTANTS.ai,
  );

  if (hasAdDimensions && isEIModule) {
    const metricsToNA = metrics.filter((metric) =>
      METRICS_TYPES_EXCLUDE_BY_FILTER[DIMENSION_TYPES_CONSTANTS.ai].includes(
        metricsByTypes[metric],
      ),
    );
    yield put(actions.setDimensionsMetricsToNA(metricsToNA || []));
  }
}

function* setDimensionsSelectedDimension(action) {
  const { dimension } = action;
  const existingDimensions = yield select(dimensionsSelector);

  yield setNAMetricsForDimension({
    dimensions: [...existingDimensions, dimension],
  });
}

function* watchUpdateDimension() {
  yield takeLatest([types.UPDATE_DIMENSION], updateDimension);
}

function* watchSetDimensions() {
  yield takeLatest([types.SET_DIMENSIONS], setNAMetricsForDimension);
}

function* watchSetSelectedDimension() {
  yield takeLatest([types.SET_SELECTED_DIMENSION], setDimensionsSelectedDimension);
}

function* watchSetGlobalByMetric() {
  yield takeLatest([types.SET_GLOBAL_ORDER_BY], ({ metric }) =>
    selectedMetricChart({ metricName: metric }),
  );
}

function* watchSetDimensionMetrics() {
  yield takeLatest([types.SET_DIMENSION_METRICS], setDimensionMetrics);
}

function* watchPrimaryMetric() {
  yield takeLatest([SELECTED_METRIC_CHART], primaryMetricEffects);
}

export default [
  watchUpdateDimension,
  watchSetDimensions,
  watchSetSelectedDimension,
  watchSetDimensionMetrics,
  watchPrimaryMetric,
  watchSetGlobalByMetric,
];
